import React, { Component } from 'react'
import {
  Col,
  DecisionTree,
  i18n,
  ProductList,
  Progress,
  Row,
  RRLink,
  SmallText,
  SplitView,
  Title,
  Svg,
  MediumText,
  getLocalizedProperty,
  context,
} from '@tools/wr-catalog-base'
import { get as safeGet, sortBy } from 'lodash'
import { submitProposal } from '@utils/proposals/submit-proposal'
import { getDuration, isProductPrototype } from '@utils/product-utils'
import { trackPageView } from '@utils/tracking'
import DocTitle from '@components/DocTitle'
import CategoriesDropdown from '@components/CategoriesDropdown'
import { renderToolbar } from './Toolbar'
import ProductBody from './ProductBody'
import { filterProducts, isNullOrEmptyString } from './utils'
import cn from 'classnames'
import { ProductTypeLabel } from './ProductBody/styles'

import { CATEGORY_CLASS_NAME_MAPPING, COL_SIZE, NEW_ICONS, PILL_COLORS, SHOW_DECISION_TREE } from './consts'
import {
  DecisionTreeContainer,
  FeaturedItemCard,
  FeaturedItemsHeader,
  RegionalFilterMessage,
  StyledList,
  SubTitle,
  ProductListWrapper,
  ProductBadgeContainer,
  ProductBadge,
} from './styles'
import { updateContextState } from '@tools/wr-catalog-base'
import { isPartnerExperience } from '@utils/isPartnerExperience'

const renderProductBody = ({ description, props: { handleProductSelection, product, context } }) => (
  <ProductBody
    description={description}
    handleProductSelection={handleProductSelection}
    product={product}
    context={context}
  />
)

const renderProductHeaderTitle = ({ props }) => {
  const category = CATEGORY_CLASS_NAME_MAPPING.find(({ category: categoryName }) =>
    props.product.categories.some((category) => category.name.toLowerCase() === categoryName),
  )

  return (
    <div style={{ display: 'flex' }}>
      {category ? (
        <div className={cn('product-card-icon', category.className)}>
          <Svg src={NEW_ICONS[category.className]} width="100%" />
        </div>
      ) : (
        <></>
      )}

      <MediumText className="product-name">
        <a>{getLocalizedProperty(props.product, 'name')}</a>
      </MediumText>
    </div>
  )
}

const getMainCategory = (product) =>
  CATEGORY_CLASS_NAME_MAPPING.find(({ category: categoryName }) =>
    product.categories.some((category) => category.name.toLowerCase() === categoryName),
  )

const PILL_MENU_LIST_STYLES = {
  menuList: (base) => ({
    ...base,
    maxHeight: 310,
  }),
}

const PILL_STYLES = {
  multiValueLabel: (styles) => ({ ...styles, color: '--color-black-secondary' }),
  multiValue: (styles, { data }) => {
    const catClassMapping = CATEGORY_CLASS_NAME_MAPPING.find((categoryClassName) =>
      categoryClassName.category.includes(data.name.toLowerCase()),
    )
    const color = PILL_COLORS[catClassMapping?.className]
    if (!color) return styles
    return {
      ...styles,
      backgroundColor: color,
    }
  },
  ...PILL_MENU_LIST_STYLES,
}

class List extends Component {
  componentDidMount() {
    this.props.context.actions.logIndexTransaction()

    if (isPartnerExperience() && process.env.NODE_ENV !== 'development') {
      updateContextState((state) => {
        context.constants.client.catalogIds = [process.env.REACT_APP_WR_PARTNER_CATALOG_ID]
        state.selectedCatalogId = process.env.REACT_APP_WR_PARTNER_CATALOG_ID
      })
    }
  }

  cacheAndGet = (currentValue, key) => {
    this._cache = this._cache || {}
    if (currentValue !== undefined) {
      if (currentValue !== this._cache[key]) {
        localStorage.setItem(`_cache:${key}`, currentValue)
      }
      return currentValue
    } else {
      if (!(key in this._cache)) {
        this._cache[key] = localStorage.getItem(`_cache:${key}`)
      }
    }
    return this._cache[key]
  }

  onNextQuestion = (q) => {
    const { context } = this.props

    if (q.final && q.type === 'catalog') {
      this.resetDecisionTree()
      this.toggle('main')
    }

    if (q.final && q.type === 'bundle' && q.answer && q.answer.childValue) {
      context.history.push(context.getRoutePath('bundleDetail', { bundleId: q.answer.childValue }))
    }

    if (q.final && q.type === 'url' && q.answer.childValue) {
      window.location = q.answer.childValue
    }

    if (q.final && q.type === 'product') {
      context.history.push(context.getRoutePath('productDetail', { productId: q.productId }))
    }

    if (q.final && q.type === 'flow' && q.answer && q.answer.childValue) {
      context.history.push(context.getRoutePath('decisionTree', { flowId: q.answer.childValue }))
    }
  }

  getToggleView = (toggle) => {
    this.toggle = toggle
  }

  getDecisionTreeReset = (resetDecisionTree) => {
    this.resetDecisionTree = resetDecisionTree
  }

  render() {
    const bodyEl = window.document.getElementsByTagName('body')[0]
    bodyEl.className = ''
    const loginBox = window.document.getElementsByClassName('loginBox')[0]
    if (loginBox) {
      loginBox.style.display = 'flex'
    }

    const {
      context,
      context: {
        utils: { getRegionName },
        state: { catalogs, selectedCatalogId, categories: ctxCategories, isCatalogExportLoading },
      },
    } = this.props

    const catalog = catalogs.find((cat) => cat.id === selectedCatalogId)

    const categories = sortBy(ctxCategories, 'name')

    const serviceType = categories.find((item) => item.tags?.trim().includes('service_type_dropdown'))
    const serviceCategory = categories.find((item) => item.tags?.trim().includes('service_category_dropdown'))
    const product = categories.find((item) => item.tags?.trim().includes('product_dropdown'))
    const segment = categories.find((item) => item.tags?.trim().includes('segments_dropdown'))

    const segments = {
      entries: categories.filter((item) => item.parentCategoryId && item.parentCategoryId === segment?.id),
      name: segment?.name,
    }
    const products = {
      entries: categories.filter((item) => item.parentCategoryId && item.parentCategoryId === product?.id),
      name: product?.name,
    }
    const serviceTypes = {
      entries: categories.filter((item) => item.parentCategoryId && item.parentCategoryId === serviceType?.id),
      name: serviceType?.name,
    }
    const serviceCategories = {
      entries: categories.filter((item) => item.parentCategoryId && item.parentCategoryId === serviceCategory?.id),
      name: serviceCategory?.name,
    }

    const regionName = getRegionName()

    const productList = (
      <ProductList
        toolBarColSize={COL_SIZE}
        searchableKeys={['name', 'description', 'customSearchable']}
        categoriesColSize={COL_SIZE}
        showLayoutButtons
        showParentChildCatergories
        showBundles
        showFilters
        showSortingButton
        showAddedToOrderIcon={false}
        showAddedToOrderBadge={false}
        showPriceFilter
        toolbar
        toolbarRenderer={renderToolbar}
        renderProductBody={renderProductBody}
        renderCustomProductHeaderTitle={renderProductHeaderTitle}
        hideButtons
        displayPriceOnList={({ product }) => {
          if (product.groupType) return

          const regionName = this.props.context.utils.getRegionName()
          const pricingRow = product.rows.find((r) => r.rowType === 'component:pricing')
          const points = this.props.context.utils.product.getPointPrice(product)
          const hours = getDuration(product)

          const productListPriceDuration =
            !points && regionName
              ? !!pricingRow
                ? isNullOrEmptyString(pricingRow.control.constants.find((cons) => cons.name === regionName)?.value)
                  ? safeGet(pricingRow.control, 'schedule.serviceRateLabel')
                  : 'Not available in Region'
                : ''
              : !!(points || hours) && `${points} points | ${hours} hours`

          if (!productListPriceDuration) return <></>

          return (
            <SmallText as="div" className="product-list-price-duration">
              {productListPriceDuration}
            </SmallText>
          )
        }}
        filterProducts={filterProducts}
        categoryClassNameMapping={CATEGORY_CLASS_NAME_MAPPING}
      >
        {(renderParts) => {
          return (
            <>
              <DocTitle title="PTC" onChange={trackPageView} />
              <div>
                {isCatalogExportLoading && (
                  <RegionalFilterMessage>
                    <Progress />
                  </RegionalFilterMessage>
                )}
              </div>
              {renderParts.toolbar}
              {regionName && <RegionalFilterMessage>Regional Filter: {regionName}</RegionalFilterMessage>}
              {renderParts.priceFilter}
              {renderParts.productList}
              {renderParts.noProductsMessage}
            </>
          )
        }}
      </ProductList>
    )

    return (
      <StyledList>
        <div className="hero">
          <div
            // This makes sure to render children over the gradient elements
            style={{ position: 'relative', zIndex: 1 }}
          >
            <div className="titles">
              <div className="hero-container">
                <Row>
                  <h1 className="title">{this.cacheAndGet(safeGet(catalog, 'name'), 'catalog:name') || '\xa0'}</h1>
                  <h2 className="subtitle">
                    {this.cacheAndGet(safeGet(catalog, 'description'), 'catalog:description') || '\xa0'}
                  </h2>
                </Row>
              </div>
            </div>
            <div className="container" style={{ textAlign: 'center', paddingTop: 30 }}>
              <FeaturedItemsHeader>Featured Services</FeaturedItemsHeader>
              <Row center="xs" style={{ padding: '20px 0' }}>
                {catalog?.contents?.items
                  ?.filter((p) => p.isFeatured)
                  .map((item, i) => {
                    const category = getMainCategory(item) || {}
                    const productCategoryName =
                      item.categories.find((cat) => category.category === cat.name.toLowerCase())?.name || ''
                    const url = context.getRoutePath('productDetail', { productId: item.id })
                    const isAdded = context.state.cart.some((v) => v.id === item.id)
                    const isPrototype = isProductPrototype(item)

                    return (
                      <Col key={item.id} {...context.constants.product.list.gridColSizes}>
                        <FeaturedItemCard as={RRLink} to={url}>
                          {isPrototype && (
                            <ProductBadgeContainer>
                              <ProductBadge isFeatured>Prototype</ProductBadge>
                            </ProductBadgeContainer>
                          )}
                          <div className={cn('featured-card-icon', category.className)}>
                            {category.className && (
                              <Svg
                                src={NEW_ICONS[category.className]}
                                width={category.className === 'vrd' ? 136 : 48}
                              />
                            )}
                          </div>
                          <MediumText className="product-name">{item.name}</MediumText>
                          <ProductTypeLabel>{productCategoryName}</ProductTypeLabel>
                          {isAdded && <div className="added-badge">{i18n('productDetails.addedToSOW')}</div>}
                          <div className="learn-more">Learn More</div>
                        </FeaturedItemCard>
                      </Col>
                    )
                  })}
              </Row>
            </div>
            <div className="categories">
              <div className="categories-container">
                {categories && !!categories.length && (
                  <Row>
                    {[
                      [segments.entries, segments.name, PILL_MENU_LIST_STYLES],
                      [products.entries, products.name, PILL_MENU_LIST_STYLES],
                      [serviceTypes.entries, serviceTypes.name, PILL_MENU_LIST_STYLES],
                      [serviceCategories.entries, serviceCategories.name, PILL_STYLES],
                    ].map(
                      ([cats, label, styles]) =>
                        !!cats.length && (
                          <Col xs key={label} className="col">
                            <div className="parent">{label}</div>
                            <div className="list">
                              <CategoriesDropdown
                                filterByCategoryLabel={`Filter by ${label}`}
                                categories={cats.map((cat) => ({ ...cat, parentCategoryId: null }))}
                                {...(styles && { styles })}
                              />
                            </div>
                          </Col>
                        ),
                    )}
                  </Row>
                )}
              </div>
            </div>
          </div>
        </div>
        {SHOW_DECISION_TREE ? (
          <SplitView
            mainLabel="I know what I am looking for"
            secondaryLabel="I need help"
            getToggleView={this.getToggleView}
            className="split-view"
          >
            {({ MainView, SecondaryView }) => (
              <>
                <MainView>
                  <ProductListWrapper>{productList}</ProductListWrapper>
                </MainView>

                <SecondaryView>
                  <div className="wr__decision_view">
                    <Title className="title">Service Discovery</Title>
                    <Row middle="xs" center="xs">
                      <Col lg={8} md={8} sm={12} xs={12}>
                        <SubTitle>
                          Welcome to the PTC Services Discovery Portal. Answer the following questions and we will guide
                          you to the service(s) that are right for you
                        </SubTitle>
                      </Col>
                    </Row>
                    <DecisionTreeContainer>
                      <DecisionTree
                        asDoc={false}
                        submitProposal={submitProposal}
                        flowId={''}
                        onNextQuestion={this.onNextQuestion}
                        getDecisionTreeReset={this.getDecisionTreeReset}
                      />
                    </DecisionTreeContainer>
                  </div>
                </SecondaryView>
              </>
            )}
          </SplitView>
        ) : (
          <ProductListWrapper>{productList}</ProductListWrapper>
        )}
      </StyledList>
    )
  }
}

export default List
